import React, { useRef } from 'react';
import { ShieldCheck, Lock, CreditCard, Globe, Code, BarChart3 } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { features } from '../mock/data';

const iconComponents = {
  ShieldCheck,
  Lock,
  CreditCard,
  Globe,
  Code,
  BarChart3
};

const Features = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const colors = [
    { from: 'from-purple-500', to: 'to-pink-500', glow: 'shadow-purple-500/50' },
    { from: 'from-cyan-500', to: 'to-blue-500', glow: 'shadow-cyan-500/50' },
    { from: 'from-pink-500', to: 'to-red-500', glow: 'shadow-pink-500/50' },
    { from: 'from-green-500', to: 'to-emerald-500', glow: 'shadow-green-500/50' },
    { from: 'from-orange-500', to: 'to-yellow-500', glow: 'shadow-orange-500/50' },
    { from: 'from-indigo-500', to: 'to-purple-500', glow: 'shadow-indigo-500/50' }
  ];

  return (
    <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '50px 50px'
        }}
      />

      <div className="absolute top-20 left-20 w-96 h-96 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-br from-pink-500/20 to-orange-500/20 rounded-full blur-3xl pointer-events-none" />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16 md:mb-20"
        >
          <motion.h2
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 px-1"
            style={{ fontFamily: '"Space Grotesk", sans-serif' }}
          >
            POWERFUL{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400">
              FEATURES
            </span>
          </motion.h2>
          <motion.div
            initial={{ width: 0 }}
            animate={isInView ? { width: 96 } : { width: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="h-1.5 bg-gradient-to-r from-cyan-500 via-pink-500 to-purple-500 mx-auto rounded-full mb-4"
          ></motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto text-justify hyphens-auto"
          >
            Everything you need to process payments securely and efficiently
          </motion.p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, index) => {
            const IconComponent = iconComponents[feature.icon];
            const color = colors[index];
            
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 50, rotateX: -20 }}
                animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : { opacity: 0, y: 50, rotateX: -20 }}
                transition={{ duration: 0.6, delay: 0.1 + index * 0.1 }}
                whileHover={{ 
                  scale: 1.05, 
                  y: -15,
                  rotateY: 5,
                  boxShadow: `0 40px 80px ${color.glow}`
                }}
                className="group relative bg-white/5 backdrop-blur-xl p-6 sm:p-8 rounded-2xl sm:rounded-3xl border border-white/10 transition-all duration-500 overflow-hidden text-left"
                style={{ perspective: '1000px' }}
              >
                {/* Animated gradient overlay */}
                <motion.div
                  className={`absolute inset-0 bg-gradient-to-br ${color.from} ${color.to} opacity-0 group-hover:opacity-20 transition-opacity duration-500`}
                ></motion.div>

                {/* Logo + title row */}
                <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4 min-w-0">
                  <motion.div
                    whileHover={{ rotateY: 360, scale: 1.2 }}
                    transition={{ duration: 0.8 }}
                    className={`relative flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-gradient-to-br ${color.from} ${color.to} rounded-xl sm:rounded-2xl flex items-center justify-center shadow-2xl ${color.glow}`}
                    style={{ transformStyle: 'preserve-3d' }}
                  >
                    <IconComponent className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white" />

                    <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent rounded-xl sm:rounded-2xl pointer-events-none" />
                  </motion.div>

                  <h3
                    className="text-sm sm:text-base md:text-lg lg:text-xl font-black text-white flex-1 min-w-0 leading-tight"
                    style={{ fontFamily: '"Space Grotesk", sans-serif' }}
                  >
                    {feature.title}
                  </h3>
                </div>

                {/* Description */}
                <p className="text-gray-300 leading-relaxed text-sm text-left hyphens-auto">{feature.description}</p>

                <div
                  className={`absolute -bottom-4 -right-4 w-24 h-24 bg-gradient-to-br ${color.from} ${color.to} rounded-full blur-2xl opacity-30 pointer-events-none`}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features;